import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import ReactDOM from "react-dom/client";
import maplibregl, {
  MapLayerMouseEvent,
  NavigationControl,
} from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import colombiaMapService from "../../services/ColombiaMapService";
import PopupDetail from "../PopupDetail/PopupDetail";
import darkColombia from "../../assets/styles/basic.json";
import { POPULATION_DATA } from "../../shared/population-colors";
import type { StyleSpecification } from "maplibre-gl";
import type {
  Feature,
  FeatureCollection,
  Point,
  Geometry,
  GeoJsonProperties,
} from "geojson";

export type GeoJsonFeature = Feature<Point, GeoJsonProperties>;
type ServiceGeoJsonData = FeatureCollection<Geometry | null, GeoJsonProperties>;

const isPointFeature = (
  f: Feature<Geometry | null, GeoJsonProperties>
): f is Feature<Point, GeoJsonProperties> =>
  !!f.geometry && f.geometry.type === "Point";

const toPointCollection = (
  fc: ServiceGeoJsonData
): FeatureCollection<Point, GeoJsonProperties> => ({
  type: "FeatureCollection",
  features: (fc.features ?? [])
    .filter(isPointFeature)
    .map((f) => ({
      type: "Feature" as const,
      geometry: f.geometry as Point,
      properties: (f.properties ?? {}) as GeoJsonProperties,
    })),
});

const getPointCoords = (
  f: { geometry?: Geometry | null }
): [number, number] | null => {
  if (!f?.geometry || f.geometry.type !== "Point") return null;
  return (f.geometry as Point).coordinates as [number, number];
};

export type ColombiaMapHandle = {
  getMap: () => maplibregl.Map | null;
};

type ColombiaMapProps = {
  onZoomChange?: (z: number) => void;
  externalZoom?: number;
  legendCollapsed?: boolean;
  legendOpenWidthPx?: number;
  rightGapPx?: number;
  rightGapCollapsedPx?: number;
  rightGapExpandedPx?: number;
  controlsTopPx?: number;
};

const ColombiaMap = forwardRef<ColombiaMapHandle, ColombiaMapProps>(
  (
    {
      onZoomChange,
      externalZoom,
      legendCollapsed,
      legendOpenWidthPx,
      rightGapPx,
      rightGapCollapsedPx,
      rightGapExpandedPx,
      controlsTopPx,
    },
    ref
  ) => {
    const mapContainer = useRef<HTMLDivElement>(null);
    const map = useRef<maplibregl.Map | null>(null);
    const navControlRef = useRef<NavigationControl | null>(null);
    const placeNavRef = useRef<(() => void) | null>(null);
    const latestLegendCollapsedRef = useRef(legendCollapsed ?? false);
    const latestLegendWidthRef = useRef(legendOpenWidthPx ?? 0);
    const initialCollapsedGap = rightGapCollapsedPx ?? rightGapPx ?? 16;
    const initialExpandedGap = rightGapExpandedPx ?? rightGapPx ?? 10;
    const latestRightGapCollapsedRef = useRef(initialCollapsedGap);
    const latestRightGapExpandedRef = useRef(initialExpandedGap);
    const lastIsMobileRef = useRef<boolean | null>(null);
    const lastRightPxRef = useRef<number | null>(null);
    const [isGeoJsonLoaded, setIsGeoJsonLoaded] = useState(false);
    const [userMarker, setUserMarker] = useState<maplibregl.Marker | null>(null);

    const selectedMarkerRef = useRef<maplibregl.Marker | null>(null);
    const popupRef = useRef<maplibregl.Popup | null>(null);
    const popupRootRef = useRef<ReactDOM.Root | null>(null);
    const styleJson = darkColombia as unknown as StyleSpecification;

    useEffect(() => {
      latestLegendCollapsedRef.current = !!legendCollapsed;
    }, [legendCollapsed]);

    useEffect(() => {
      latestLegendWidthRef.current = legendOpenWidthPx ?? 0;
    }, [legendOpenWidthPx]);

    useEffect(() => {
      if (typeof rightGapPx === "number") {
        latestRightGapCollapsedRef.current = rightGapPx;
        latestRightGapExpandedRef.current = rightGapPx;
      }
    }, [rightGapPx]);

    useEffect(() => {
      if (typeof rightGapCollapsedPx === "number") {
        latestRightGapCollapsedRef.current = rightGapCollapsedPx;
      }
    }, [rightGapCollapsedPx]);

    useEffect(() => {
      if (typeof rightGapExpandedPx === "number") {
        latestRightGapExpandedRef.current = rightGapExpandedPx;
      }
    }, [rightGapExpandedPx]);

    const setCursor = useCallback((style: string) => {
      if (map.current) map.current.getCanvas().style.cursor = style;
    }, []);

    const createLocationMarker = useCallback(() => {
      const el = document.createElement("div");
      el.style.width = "20px";
      el.style.height = "20px";
      el.style.backgroundColor = "#4285f4";
      el.style.border = "3px solid white";
      el.style.borderRadius = "50%";
      el.style.boxShadow = "0 0 10px rgba(66, 133, 244, 0.6)";
      return el;
    }, []);

    const getPopulationColor = useCallback((population: string) => {
      const normalizedPopulation = population?.toLowerCase() || '';
      
      if (normalizedPopulation.includes('niños')) return POPULATION_DATA['Niños, Niñas y/o Adolescentes'].color;
      if (normalizedPopulation.includes('discapacidad')) return POPULATION_DATA['Personas con Discapacidad'].color;
      if (normalizedPopulation.includes('mayores')) return POPULATION_DATA['Personas Mayores'].color;
      if (normalizedPopulation.includes('cuidador')) return POPULATION_DATA['Cuidadores'].color;
      
      return "#CE0047";
    }, []);

    const createSelectedMarkerEl = useCallback((populationColor?: string) => {
      const el = document.createElement("div");
      el.style.width = "24px";
      el.style.height = "36px";
      el.style.display = "block";
      el.style.pointerEvents = "auto";
      const color = populationColor || "#CE0047";
      el.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="24" height="36">
          <path fill="${color}" d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24C24 5.4 18.6 0 12 0z"/>
          <circle fill="white" cx="12" cy="12" r="5"/>
        </svg>
      `;
      return el;
    }, []);

    const updateSelectedMarkerPosition = useCallback((coords: [number, number], feature?: GeoJsonFeature) => {
      if (!map.current) return;

      const populationColor = feature?.properties?.["Población atendida"] 
        ? getPopulationColor(feature.properties["Población atendida"])
        : "#CE0047";

      if (selectedMarkerRef.current) {
        selectedMarkerRef.current.setLngLat(coords);
        const markerEl = createSelectedMarkerEl(populationColor);
        selectedMarkerRef.current.getElement().innerHTML = markerEl.innerHTML;
      } else {
        const markerEl = createSelectedMarkerEl(populationColor);
        selectedMarkerRef.current = new maplibregl.Marker({
          element: markerEl,
          anchor: "bottom",
          offset: [0, 0],
        })
          .setLngLat(coords)
          .addTo(map.current);
      }
    }, [createSelectedMarkerEl, getPopulationColor]);

    const centerMapOnResult = useCallback((feat: GeoJsonFeature) => {
      const coords = getPointCoords(feat);
      if (!coords || !map.current) return;
      const [lng, lat] = coords;
      map.current.flyTo({
        center: [lng, lat],
        zoom: 14,
        duration: 900,
        essential: true,
      });
    }, []);

    const centerMapOnCoord = useCallback((lat: number, lng: number) => {
      map.current?.flyTo({
        center: [lng, lat],
        zoom: 14,
        duration: 800,
        essential: true,
      });
    }, []);

    const closePopup = useCallback(() => {
      try {
        popupRootRef.current?.unmount();
      } catch {}
      popupRootRef.current = null;
      if (popupRef.current) {
        popupRef.current.remove();
        popupRef.current = null;
      }
    }, []);

    const openPopupForFeature = useCallback(
      (feat: GeoJsonFeature) => {
        if (!map.current) return;
        const coords = getPointCoords(feat);
        if (!coords) return;
    
        closePopup();
    
        const container = document.createElement("div");
        popupRootRef.current = ReactDOM.createRoot(container);
        popupRootRef.current.render(
          <PopupDetail feature={feat} onClose={closePopup} />
        );
    
        popupRef.current = new maplibregl.Popup({
          closeButton: false,
          closeOnClick: true,
          anchor: "bottom",
          offset: [0, -10],
          className: "custom-popup",
        })
          .setLngLat(coords)
          .setDOMContent(container)
          .addTo(map.current!);
      },
      [closePopup]
    );
    

    const loadGeoJsonToMap = useCallback(
      (data: ServiceGeoJsonData) => {
        if (!map.current) return;

        const add = () => {
          if (map.current!.getSource("geojson-data")) {
            ["unclustered-points", "cluster-count", "clusters"].forEach((id) => {
              if (map.current!.getLayer(id)) map.current!.removeLayer(id);
            });
            map.current!.removeSource("geojson-data");
          }

          const pointData = toPointCollection(data);

          map.current!.addSource("geojson-data", {
            type: "geojson",
            data: pointData,
            cluster: true,
            clusterMaxZoom: 8,
            clusterRadius: 50,
            clusterMinPoints: 2,
          });

          map.current!.addLayer({
            id: "clusters",
            type: "circle",
            source: "geojson-data",
            filter: ["has", "point_count"],
            paint: {
              "circle-color": "#2fc1de",
              "circle-radius": [
                "step",
                ["get", "point_count"],
                20,
                100,
                30,
                750,
                40,
              ] as any,
            },
          });

          map.current!.addLayer({
            id: "cluster-count",
            type: "symbol",
            source: "geojson-data",
            filter: ["has", "point_count"],
            layout: {
              "text-field": "{point_count_abbreviated}",
              "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
              "text-size": 12,
              "text-allow-overlap": true,
              "text-ignore-placement": true,
            },
            paint: { "text-color": "#fff" },
          });

          map.current!.addLayer({
            id: "unclustered-points",
            type: "circle",
            source: "geojson-data",
            filter: ["!", ["has", "point_count"]],
            paint: {
              "circle-radius": 7,
              "circle-stroke-color": "#FFFFFF",
              "circle-stroke-width": 1,
              "circle-opacity": 1,
              "circle-color": [
                "case",
                [
                  ">=",
                  [
                    "+",
                    [
                      "case",
                      [
                        ">=",
                        [
                          "index-of",
                          "niños",
                          [
                            "downcase",
                            [
                              "to-string",
                              ["coalesce", ["get", "Población atendida"], ""],
                            ],
                          ],
                        ],
                        0,
                      ],
                      1,
                      0,
                    ],
                    [
                      "case",
                      [
                        ">=",
                        [
                          "index-of",
                          "mayores",
                          [
                            "downcase",
                            [
                              "to-string",
                              ["coalesce", ["get", "Población atendida"], ""],
                            ],
                          ],
                        ],
                        0,
                      ],
                      1,
                      0,
                    ],
                    [
                      "case",
                      [
                        ">=",
                        [
                          "index-of",
                          "discapacidad",
                          [
                            "downcase",
                            [
                              "to-string",
                              ["coalesce", ["get", "Población atendida"], ""],
                            ],
                          ],
                        ],
                        0,
                      ],
                      1,
                      0,
                    ],
                    [
                      "case",
                      [
                        ">=",
                        [
                          "index-of",
                          "cuidador",
                          [
                            "downcase",
                            [
                              "to-string",
                              ["coalesce", ["get", "Población atendida"], ""],
                            ],
                          ],
                        ],
                        0,
                      ],
                      1,
                      0,
                    ],
                  ],
                  2,
                ],
                "#4B5563",
                [
                  ">=",
                  [
                    "index-of",
                    "niños",
                    [
                      "downcase",
                      ["to-string", ["coalesce", ["get", "Población atendida"], ""]],
                    ],
                  ],
                  0,
                ],
                "#F1C40F",
                [
                  ">=",
                  [
                    "index-of",
                    "discapacidad",
                    [
                      "downcase",
                      ["to-string", ["coalesce", ["get", "Población atendida"], ""]],
                    ],
                  ],
                  0,
                ],
                "#07BC0C",
                [
                  ">=",
                  [
                    "index-of",
                    "mayores",
                    [
                      "downcase",
                      ["to-string", ["coalesce", ["get", "Población atendida"], ""]],
                    ],
                  ],
                  0,
                ],
                "#A947E1",
                [
                  ">=",
                  [
                    "index-of",
                    "cuidador",
                    [
                      "downcase",
                      ["to-string", ["coalesce", ["get", "Población atendida"], ""]],
                    ],
                  ],
                  0,
                ],
                "#3498DB",
                "#4B5563",
              ],
            },
          });

          map.current!.on("mouseenter", "unclustered-points", () =>
            setCursor("pointer")
          );
          map.current!.on("mouseleave", "unclustered-points", () =>
            setCursor("default")
          );

          map.current!.on("click", "clusters", (e: MapLayerMouseEvent) => {
            const feature = e.features?.[0];
            if (!feature) return;
            const coords = (feature.geometry as any).coordinates as [
              number,
              number
            ];
            const clusterId = (feature.properties as any).cluster_id;
            const src = map.current!.getSource("geojson-data") as any;
            if (!src) return;

            src.getClusterExpansionZoom(clusterId, (err: any, zoom: number) => {
              if (err) return;
              map.current!.flyTo({
                center: coords,
                zoom,
                duration: 800,
                essential: true,
              });
            });
          });

          map.current!.on("click", "unclustered-points", (e: MapLayerMouseEvent) => {
            const f = e.features?.[0];
            if (!f || f.geometry?.type !== "Point") return;
            const typed: GeoJsonFeature = {
              type: "Feature",
              geometry: {
                type: "Point",
                coordinates: (f.geometry as Point).coordinates as [
                  number,
                  number
                ],
              },
              properties: (f.properties ?? {}) as GeoJsonProperties,
            };
            openPopupForFeature(typed);
          });

          setIsGeoJsonLoaded(true);
        };

        if (map.current.isStyleLoaded()) add();
        else map.current.once("load", add);
      },
      [setCursor, openPopupForFeature]
    );

    const updateGeoJsonData = useCallback(
      (data: ServiceGeoJsonData) => {
        if (!map.current || !isGeoJsonLoaded) return;
        const source = map.current.getSource(
          "geojson-data"
        ) as maplibregl.GeoJSONSource | undefined;
        if (source) {
          const pointData = toPointCollection(data);
          source.setData(pointData);
        }
      },
      [isGeoJsonLoaded]
    );

    useEffect(() => {
      if (!mapContainer.current || map.current) return;

      map.current = new maplibregl.Map({
        container: mapContainer.current,
        style: styleJson as StyleSpecification,
        center: [-74, 4],
        zoom: 2.7,
        minZoom: 0.5,
        maxZoom: 18,
        attributionControl: false,
      });

      map.current.on("load", () => {
        const CO_BOUNDS: [[number, number], [number, number]] = [
          [-84, -5],
          [-66, 14],
        ];
        map.current!.fitBounds(CO_BOUNDS, {
          padding: 40,
          duration: 0,
          maxZoom: 4.6,
        });
        map.current!.setMaxBounds(null);
        map.current!.setMinZoom(0.5);

        navControlRef.current = new NavigationControl();
        map.current!.addControl(navControlRef.current, "top-right");

        const computeRightPx = () => {
          const mobile = window.matchMedia("(max-width: 768px)").matches;
          if (mobile) return 12;
          const collapsed = latestLegendCollapsedRef.current;
          const legendWidth = collapsed ? 0 : latestLegendWidthRef.current ?? 0;
          const gap = collapsed
            ? latestRightGapCollapsedRef.current ?? 16
            : latestRightGapExpandedRef.current ?? 10;
          return legendWidth + gap;
        };

        const reattachControlIfNeeded = () => {
          const mobile = window.matchMedia("(max-width: 768px)").matches;
          if (lastIsMobileRef.current === null || lastIsMobileRef.current !== mobile) {
            if (navControlRef.current) {
              map.current!.removeControl(navControlRef.current);
              map.current!.addControl(
                navControlRef.current,
                mobile ? "bottom-right" : "top-right"
              );
            }
            lastIsMobileRef.current = mobile;
          }
        };

        placeNavRef.current = () => {
          if (!map.current || !navControlRef.current) return;

          reattachControlIfNeeded();

          const mobile = window.matchMedia("(max-width: 768px)").matches;
          const controlsTop = (typeof controlsTopPx === "number" ? controlsTopPx : 14);
          const cls = mobile
            ? ".maplibregl-ctrl-bottom-right"
            : ".maplibregl-ctrl-top-right";
          const navEl = map.current
            .getContainer()
            .querySelector(cls) as HTMLElement | null;
          if (!navEl) return;

          navEl.style.position = "absolute";
          navEl.style.zIndex = "40";
          navEl.style.transition = mobile ? "" : "right 180ms ease";

          if (mobile) {
            navEl.style.top = "auto";
            navEl.style.right = "12px";
            navEl.style.bottom = "12px";
            navEl.style.left = "auto";
            lastRightPxRef.current = null;
          } else {
            navEl.style.top = `${controlsTop}px`;
            navEl.style.bottom = "auto";
            navEl.style.left = "auto";
            const rightPx = computeRightPx();
            if (lastRightPxRef.current === null || lastRightPxRef.current !== rightPx) {
              navEl.style.right = `${rightPx}px`;
              lastRightPxRef.current = rightPx;
            }
          }

          const compassBtn = navEl.querySelector(
            ".maplibregl-ctrl-compass"
          ) as HTMLElement | null;
          if (compassBtn) {
            compassBtn.innerHTML =
              '<span class="material-icons-outlined" style="font-size:20px; padding-top:4px;">my_location</span>';
            compassBtn.title = "Centrar en tu ubicación";
            compassBtn.onclick = (e) => {
              e.preventDefault();
              e.stopPropagation();
              if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                  (pos) => {
                    map.current!.flyTo({
                      center: [pos.coords.longitude, pos.coords.latitude],
                      zoom: 14,
                      duration: 1000,
                    });
                  },
                  () => {
                    alert("No se pudo obtener la ubicación.");
                  }
                );
              } else {
                alert("La geolocalización no está disponible en tu navegador.");
              }
            };
          }
        };

        placeNavRef.current();
        window.addEventListener("resize", placeNavRef.current!);
      });

      return () => {
        try {
          popupRootRef.current?.unmount();
        } catch {}
        popupRef.current?.remove();
        popupRef.current = null;

        if (selectedMarkerRef.current) selectedMarkerRef.current.remove();
        if (userMarker) userMarker.remove();

        if (placeNavRef.current) window.removeEventListener("resize", placeNavRef.current);
        if (map.current) {
          map.current.remove();
          map.current = null;
        }
      };
    }, [styleJson, userMarker]);

    useEffect(() => {
      if (!map.current) return;
      const emit = () => onZoomChange?.(Number(map.current!.getZoom().toFixed(2)));
      map.current.on("zoom", emit);
      map.current.on("zoomend", emit);
      emit();
      return () => {
        map.current?.off("zoom", emit);
        map.current?.off("zoomend", emit);
      };
    }, [onZoomChange]);

    useEffect(() => {
      if (!map.current) return;

      const updateBounds = () => {
        if (!map.current) return;
        const bounds = map.current.getBounds();
        if (bounds) colombiaMapService.setBoundFilter(bounds);
      };

      const setupBoundsListeners = () => {
        if (!map.current) return;
        map.current.on("moveend", updateBounds);
        map.current.on("zoomend", updateBounds);
        setTimeout(updateBounds, 100);
      };

      if (map.current.isStyleLoaded()) setupBoundsListeners();
      else map.current.once("load", setupBoundsListeners);

      return () => {
        map.current?.off("moveend", updateBounds);
        map.current?.off("zoomend", updateBounds);
      };
    }, []);

    useEffect(() => {
      if (!map.current || typeof externalZoom !== "number") return;
      const minZ = map.current.getMinZoom();
      const maxZ = map.current.getMaxZoom();
      const z = Math.min(Math.max(externalZoom, minZ), maxZ);
      if (Math.abs(map.current.getZoom() - z) > 0.01) {
        map.current.flyTo({ zoom: z, duration: 250, essential: true });
      }
    }, [externalZoom]);

    useEffect(() => {
      colombiaMapService.loadGeoJsonData();

      const unsubGeo = colombiaMapService.subscribeToFilteredGeoJson((data) => {
        if (data && map.current) {
          if (!isGeoJsonLoaded) loadGeoJsonToMap(data);
          else updateGeoJsonData(data);
        }
      });

      const unsubSel = colombiaMapService.subscribeToSelectedItem((result) => {
        if (result && result.geometry?.type === "Point") {
          const typed: GeoJsonFeature = {
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: (result.geometry as Point).coordinates as [number, number],
            },
            properties: (result.properties ?? {}) as GeoJsonProperties,
          };

          const coords = typed.geometry.coordinates as [number, number];

          centerMapOnResult(typed);

          if (popupRef.current) {
            closePopup();
            updateSelectedMarkerPosition(coords, typed);
          } else {
            if (selectedMarkerRef.current) {
              selectedMarkerRef.current.remove();
              selectedMarkerRef.current = null;
            }
            closePopup();
            updateSelectedMarkerPosition(coords, typed);
          }
        } else {
          closePopup();
          if (selectedMarkerRef.current) {
            selectedMarkerRef.current.remove();
            selectedMarkerRef.current = null;
          }
        }
      });

      const unsubPos = colombiaMapService.subscribeToCurrentPosition((pos) => {
        if (!pos) return;
        centerMapOnCoord(pos.lat, pos.lng);
        if (userMarker) {
          userMarker.setLngLat([pos.lng, pos.lat]);
        } else {
          const markerElement = createLocationMarker();
          const newMarker = new maplibregl.Marker({ element: markerElement })
            .setLngLat([pos.lng, pos.lat])
            .addTo(map.current!);
          setUserMarker(newMarker);
        }
      });

      return () => {
        unsubGeo();
        unsubSel();
        unsubPos();
      };
    }, [
      isGeoJsonLoaded,
      loadGeoJsonToMap,
      updateGeoJsonData,
      centerMapOnResult,
      centerMapOnCoord,
      createLocationMarker,
      updateSelectedMarkerPosition,
      userMarker,
      openPopupForFeature,
      closePopup,
    ]);

    useEffect(() => {
      placeNavRef.current?.();
    }, [legendCollapsed]);

    useEffect(() => {
      placeNavRef.current?.();
    }, [legendOpenWidthPx, rightGapPx, rightGapCollapsedPx, rightGapExpandedPx, controlsTopPx]);

    useEffect(() => {
      const handler = () => placeNavRef.current?.();
      window.addEventListener("orientationchange", handler);
      return () => window.removeEventListener("orientationchange", handler);
    }, []);

    useImperativeHandle(ref, () => ({
      getMap: () => map.current,
    }));

    return (
      <div className="relative w-full h-full">
        <div ref={mapContainer} className="w-full h-full absolute inset-0" />
      </div>
    );
  }
);

export default ColombiaMap;
